import { lazy } from 'react';

const CHUNK_RELOAD_FLAG = 'chunk_failure_reload_at';
const CHUNK_RELOAD_WINDOW_MS = 60000;

const delay = (ms) => new Promise((resolve) => {
  setTimeout(resolve, ms);
});

// One guarded full reload per minute: a reload re-fetches index.html, which
// self-heals stale-deploy chunk references. The sessionStorage window keeps a
// persistent failure (user offline, chunk really gone) from reload-looping.
const tryReloadOnce = () => {
  try {
    const lastReloadAt = Number(window.sessionStorage.getItem(CHUNK_RELOAD_FLAG)) || 0;

    if (Date.now() - lastReloadAt < CHUNK_RELOAD_WINDOW_MS) {
      return false;
    }

    window.sessionStorage.setItem(CHUNK_RELOAD_FLAG, String(Date.now()));
  } catch {
    // sessionStorage unavailable — reload without the loop guard would be
    // riskier than surfacing the error, so let the error boundary handle it.
    return false;
  }

  window.location.reload();

  return true;
};

// Drop-in replacement for React.lazy that survives transient chunk-load
// failures (flaky networks, deploys that replaced hashed chunk files):
// retry the import once after a short pause, then fall back to a single
// guarded page reload. Only if all of that fails does the error propagate
// (to the app-level error boundary).
const lazyWithRetry = (importFn) => lazy(
  () => importFn()
    .catch(() => delay(1500).then(importFn))
    .catch((error) => {
      if (tryReloadOnce()) {
        // The page is reloading; keep Suspense showing its fallback meanwhile.
        return new Promise(() => {});
      }

      throw error;
    }),
);

export default lazyWithRetry;
